import type { NotFoundError } from 'rxjs';

import { Data, Duration, Effect, Predicate, Schedule } from 'effect';

import type {
  ServerError,
  ViolationError,
} from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { ErrorTag } from '@vs-shop/engine/data/error/index';

/**
 * Shared TypeId for TokenRemovedRetryError
 */
export const TokenRemovedRetryErrorTypeId: unique symbol = Symbol.for(
  '@app/OrderClient/TokenRemovedRetryError'
);
export type TokenRemovedRetryErrorTypeId = typeof TokenRemovedRetryErrorTypeId;

/**
 * Signals that order token was removed and operation should be retried.
 * Used to distinguish between errors that cleaned up state (safe to retry)
 * and errors that didn't (should not retry).
 */
export class TokenRemovedRetryError extends Data.TaggedError(ErrorTag.TokenRemovedRetryError)<{
  readonly originalError: NotFoundError | ViolationError | ServerError;
}> {
  readonly [TokenRemovedRetryErrorTypeId]: TokenRemovedRetryErrorTypeId =
    TokenRemovedRetryErrorTypeId;
}

/**
 * Type guard to check if error is TokenRemovedRetryError
 */
export const isTokenRemovedRetryError = (u: unknown): u is TokenRemovedRetryError =>
  Predicate.hasProperty(u, TokenRemovedRetryErrorTypeId);

export const retryPolicy = Schedule.recurs(2).pipe(
  Schedule.addDelay(() => Effect.succeed(Duration.millis(100))),
  Schedule.while((meta) => isTokenRemovedRetryError(meta.input))
);
